import type { ReactNode } from 'react';
import '../../styles/studio.css';

/**
 * Pieces of the Content Studio shown through the portal: a 9:16 video draft
 * (a thumbnail with a play icon and "Watch", as the app links to the video
 * today), a LinkedIn carousel with arrows, and the "made in the Content
 * Studio" run card. Sample content only.
 */

export const THUMBS = {
  mistakes: '/img/studio/thumb-mistakes.webp',
  wait: '/img/studio/thumb-wait.webp',
  weekend: '/img/studio/thumb-weekend.webp',
} as const;

export const CAROUSEL = [
  { big: '5 ways we cut our wait time', small: 'Named tips' },
  { big: '1. The two-minute rule', small: 'Answer every request fast.' },
  { big: '2. Book the next visit before they leave', small: 'No chasing later.' },
  { big: '3. One text, not three calls', small: 'Short, clear, done.' },
  { big: 'Want the checklist? Comment “list”', small: '5 slides, ready as a PDF' },
];

const Play = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" fill="#fff" /></svg>
);

export const VideoTile = ({ src, dur, note }: { src: string; dur: string; note?: ReactNode }) => (
  <div className="st-video">
    <span className="st-thumb">
      <img src={src} alt="" width={540} height={960} loading="lazy" decoding="async" draggable={false} />
      <span className="st-play"><Play /></span>
      <span className="st-dur">{dur}</span>
    </span>
    <span className="st-video-side">
      <span className="st-watch">Watch</span>
      <span className="st-meta">9:16 &middot; captions on</span>
      {note && <span className="st-note">{note}</span>}
    </span>
  </div>
);

export const CarouselPreview = ({ slide, onSlide }: { slide: number; onSlide?: (n: number) => void }) => {
  const s = CAROUSEL[slide];
  const go = (d: number) => onSlide?.((slide + d + CAROUSEL.length) % CAROUSEL.length);
  return (
    <div className="st-carousel">
      <button type="button" className="st-arrow" aria-label="Previous slide" onClick={() => go(-1)}>&lsaquo;</button>
      <div className="st-slide" key={slide}>
        <small>{slide + 1} / {CAROUSEL.length}</small>
        <b>{s.big}</b>
        <span>{s.small}</span>
      </div>
      <button type="button" className="st-arrow" aria-label="Next slide" onClick={() => go(1)}>&rsaquo;</button>
    </div>
  );
};

export const RUN_STAGES = ['Listening to it', 'Editing', 'Rendering', 'Ready to review'] as const;

export const RunCard = ({ stage }: { stage: number }) => (
  <div className="st-run a-card">
    <p className="a-caps">Made in the Content Studio</p>
    <b>Kitchen tips (raw recording)</b>
    <div className="st-stages" role="list">
      {RUN_STAGES.map((s, i) => (
        <span key={s} role="listitem" className={`st-stage${i < stage ? ' done' : i === stage ? ' now' : ''}`}>{s}</span>
      ))}
    </div>
    <span className="st-sum">
      {stage >= 3 ? '0:53 → 0:38 · 14 cuts · opens on its strongest line · 3 Shorts cut from it' : 'Dead air and filler cut, captions on, strongest line first.'}
    </span>
  </div>
);
